import { css, html, LitElement } from 'lit';
import { navigator as nav } from 'lit-element-router';

import { RequesterMixin } from '@brightspace-ui/core/mixins/provider-mixin';

import '@brightspace-ui/core/components/button/button.js';
import '@brightspace-ui/core/components/button/button-subtle.js';
import '@brightspace-ui/core/components/dialog/dialog.js';
import '@brightspace-ui/core/components/inputs/input-text.js';
import '@brightspace-ui/core/components/inputs/input-select-styles.js';

import './environment.js';
import './task-manager.js';
import './jwt-decoder.js';

/**
 * Environment Shell - New UI for environment detail page
 * Hybrid dark shell with D2L components inside
 */
class EnvironmentShell extends RequesterMixin(nav(LitElement)) {

  static get properties() {
    return {
      environmentId: { type: String, attribute: 'environment-id' },
      environment: { type: Object },
      hasTaskManagement: { type: Boolean, attribute: 'has-task-management' },
      _activePanel: { type: String, state: true },
      _healthStatus: { type: Object, state: true },
      _deployments: { type: Array, state: true },
      _systemTokens: { type: Object, state: true },
      _activeServiceTab: { type: Object, state: true },
      _activeService: { type: String, state: true },
      _toastMessage: { type: String, state: true },
      _toastVisible: { type: Boolean, state: true },
      _searchQuery: { type: String, state: true },
      _searchFocused: { type: Boolean, state: true },
      _searchResults: { type: Object, state: true },
      _searchDropdownOpen: { type: Boolean, state: true },
      _isDemoResetEnv: { type: Boolean, state: true },
      _advancedView: { type: Boolean, state: true },
    };
  }

  static get styles() {
    return css`
      :host {
        display: block;
        --shell-bg: #ffffff;
        --shell-panel: #ffffff;
        --shell-text: #1f2937;
        --shell-muted: #6b7280;
        --shell-muted2: #9ca3af;
        --shell-border: rgba(0, 0, 0, 0.08);
        --shell-border2: rgba(0, 0, 0, 0.12);
        --shell-accent: #7c3aed;
        --shell-accent2: #6d28d9;
        --shell-good: #16a34a;
        --shell-warn: #d97706;
        --shell-bad: #dc2626;
        --shell-radius: 16px;
        --shell-radius2: 12px;
        --shell-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
        --shell-focus: 0 0 0 3px rgba(124, 58, 237, 0.25);
      }

      .shell-app {
        min-height: 100vh;
        background: var(--shell-bg);
        color: var(--shell-text);
        font-family: ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif;
      }

      /* Header */
      .shell-header {
        background: #ffffff;
        border-bottom: 1px solid #e5e7eb;
        margin-bottom: 0;
      }

      .header-inner {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 12px;
        padding: 12px 18px;
        max-width: 1280px;
        margin: 0 auto;
      }

      .env-info {
        display: flex;
        align-items: center;
        gap: 16px;
      }

      .env-title-main {
        font-size: 18px;
        font-weight: 700;
        margin: 0;
        color: var(--shell-text);
      }

      .env-badges {
        display: flex;
        gap: 8px;
        align-items: center;
      }

      .header-search {
        position: relative;
        display: flex;
        align-items: center;
      }

      .header-search svg {
        position: absolute;
        left: 10px;
        opacity: 0.5;
        pointer-events: none;
      }

      .header-search input {
        width: 200px;
        padding: 8px 70px 8px 32px;
        border-radius: 8px;
        border: 1px solid #e5e7eb;
        background: #f9fafb;
        color: var(--shell-text);
        font-size: 13px;
        outline: none;
        transition: width 0.2s ease, border-color 0.15s ease;
      }

      .header-search input:focus {
        width: 280px;
        border-color: rgba(124, 58, 237, 0.4);
        box-shadow: var(--shell-focus);
        background: #ffffff;
      }

      .header-search input::placeholder {
        color: var(--shell-muted2);
      }

      .search-shortcut {
        position: absolute;
        right: 8px;
        display: flex;
        gap: 2px;
        pointer-events: none;
        opacity: 0.7;
      }

      .header-search input:focus + .search-shortcut {
        opacity: 0;
      }

      /* Search dropdown */
      .search-dropdown {
        position: absolute;
        top: calc(100% + 8px);
        left: 0;
        width: 420px;
        max-height: 400px;
        overflow-y: auto;
        background: #ffffff;
        border: 1px solid #e5e7eb;
        border-radius: 16px;
        box-shadow: 0 12px 40px rgba(0, 0, 0, 0.15);
        z-index: 100;
        display: none;
      }

      .search-dropdown.open {
        display: block;
      }

      .search-group {
        padding: 8px 0;
        border-left: 4px solid transparent;
      }

      .search-group:not(:last-child) {
        border-bottom: 1px solid #f3f4f6;
      }

      .search-group.tenants { border-left-color: #10b981; background: linear-gradient(to right, rgba(16, 185, 129, 0.04), transparent); }
      .search-group.services { border-left-color: #f59e0b; background: linear-gradient(to right, rgba(245, 158, 11, 0.04), transparent); }
      .search-group.settings { border-left-color: #8b5cf6; background: linear-gradient(to right, rgba(139, 92, 246, 0.04), transparent); }

      .search-group-label {
        padding: 6px 16px;
        font-size: 11px;
        font-weight: 700;
        text-transform: uppercase;
        letter-spacing: 0.5px;
        color: var(--shell-muted2);
      }

      .search-group.tenants .search-group-label { color: #10b981; }
      .search-group.services .search-group-label { color: #f59e0b; }
      .search-group.settings .search-group-label { color: #8b5cf6; }

      .search-result {
        display: flex;
        align-items: center;
        gap: 12px;
        padding: 10px 16px;
        cursor: pointer;
        color: var(--shell-text);
        font-size: 14px;
        font-weight: 500;
        transition: background 0.1s ease;
      }

      .search-result:hover {
        background: rgba(0, 0, 0, 0.04);
      }

      .search-result .result-icon {
        width: 28px;
        height: 28px;
        border-radius: 8px;
        display: grid;
        place-items: center;
        background: #f3f4f6;
        font-size: 13px;
        flex-shrink: 0;
      }

      .search-result .result-text {
        flex: 1;
        min-width: 0;
      }

      .search-result .result-title {
        font-weight: 600;
      }

      .search-result .result-subtitle {
        font-size: 12px;
        color: var(--shell-muted);
        margin-top: 1px;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }

      .search-result .copy-hint {
        font-size: 11px;
        color: var(--shell-muted2);
        opacity: 0;
        transition: opacity 0.15s ease;
        flex-shrink: 0;
      }

      .search-result.copyable:hover .copy-hint {
        opacity: 1;
      }

      .search-empty {
        padding: 24px 16px;
        text-align: center;
        color: var(--shell-muted);
        font-size: 13px;
      }

      .pill {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        padding: 5px 10px;
        border-radius: 999px;
        background: #f3f4f6;
        border: 1px solid #e5e7eb;
        color: var(--shell-muted);
        font-size: 12px;
      }

      .pill strong {
        color: var(--shell-text);
        font-weight: 650;
      }

      .pill.muted {
        background: transparent;
        border-color: transparent;
        color: var(--shell-muted);
      }

      .dot {
        width: 8px;
        height: 8px;
        border-radius: 999px;
        background: var(--shell-muted2);
      }

      .dot.good { background: var(--shell-good); }
      .dot.warn { background: var(--shell-warn); }
      .dot.bad { background: var(--shell-bad); }

      .header-actions {
        display: flex;
        align-items: center;
        gap: 10px;
      }

      .btn {
        appearance: none;
        border: 1px solid var(--shell-border2);
        background: #ffffff;
        color: var(--shell-text);
        padding: 10px 12px;
        border-radius: 12px;
        cursor: pointer;
        font-weight: 600;
        font-size: 13px;
        display: inline-flex;
        align-items: center;
        gap: 8px;
        transition: transform 0.05s ease, background 0.15s ease, border-color 0.15s ease;
      }

      .btn:hover {
        background: #f3f4f6;
        border-color: rgba(0, 0, 0, 0.15);
      }

      .btn:active {
        transform: translateY(1px);
      }

      .btn.primary {
        background: linear-gradient(135deg, var(--shell-accent), var(--shell-accent2));
        border-color: transparent;
        color: #ffffff;
        box-shadow: 0 4px 12px rgba(124, 58, 237, 0.25);
      }

      .btn.primary:hover {
        filter: brightness(1.05);
      }

      .btn:focus {
        outline: none;
        box-shadow: var(--shell-focus);
      }

      a.btn {
        text-decoration: none;
      }

      .kbd {
        font-size: 12px;
        color: var(--shell-muted);
        border: 1px solid var(--shell-border);
        border-bottom-color: rgba(0, 0, 0, 0.15);
        padding: 2px 6px;
        border-radius: 8px;
        background: #f9fafb;
      }

      /* Shell layout */
      .shell-content {
        max-width: 1280px;
        margin: 0 auto;
        padding: 18px;
        display: grid;
        grid-template-columns: 280px 1fr;
        gap: 16px;
      }

      .shell-content.no-sidebar {
        grid-template-columns: 1fr;
      }

      /* Sidebar */
      .sidebar {
        position: sticky;
        top: 76px;
        align-self: start;
        height: calc(100vh - 96px);
        overflow: auto;
        padding-bottom: 12px;
      }

      .side-card {
        background: var(--shell-panel);
        border: 1px solid #e5e7eb;
        border-radius: var(--shell-radius);
        box-shadow: var(--shell-shadow);
        overflow: hidden;
      }

      .side-top {
        padding: 12px 14px;
        border-bottom: 1px solid #e5e7eb;
      }

      .side-label {
        margin: 0 0 8px 0;
        font-size: 11px;
        font-weight: 700;
        text-transform: uppercase;
        letter-spacing: 0.5px;
        color: var(--shell-muted2);
      }

      .stack {
        display: flex;
        flex-wrap: wrap;
        gap: 6px;
      }

      .chip {
        display: inline-flex;
        align-items: center;
        gap: 8px;
        padding: 6px 10px;
        border-radius: 999px;
        border: 1px solid #e5e7eb;
        background: #f9fafb;
        font-size: 12px;
        color: var(--shell-muted);
      }

      /* Navigation */
      .nav {
        padding: 10px;
        display: flex;
        flex-direction: column;
        gap: 6px;
      }

      .nav-btn {
        width: 100%;
        text-align: left;
        background: transparent;
        border: 1px solid transparent;
        color: var(--shell-muted);
        padding: 10px 10px;
        border-radius: 12px;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 10px;
        font-weight: 650;
        font-size: 14px;
      }

      .nav-btn:hover {
        background: #f3f4f6;
        color: var(--shell-text);
      }

      .nav-btn.active {
        background: rgba(124, 58, 237, 0.08);
        border-color: rgba(124, 58, 237, 0.25);
        color: var(--shell-accent);
      }

      .nav-meta {
        font-size: 12px;
        color: var(--shell-muted2);
        font-weight: 700;
        border: 1px solid #e5e7eb;
        background: #f9fafb;
        padding: 2px 8px;
        border-radius: 999px;
      }

      /* Main content */
      .main-content {
        min-width: 0;
      }

      .content-card {
        background: #ffffff;
        border: 1px solid #e5e7eb;
        border-radius: var(--shell-radius);
        box-shadow: var(--shell-shadow);
        overflow: hidden;
      }

      .content-head {
        padding: 16px 18px 12px 18px;
        border-bottom: 1px solid #e5e7eb;
        display: flex;
        gap: 12px;
        align-items: flex-start;
        justify-content: space-between;
      }

      .content-head h3 {
        margin: 0;
        font-size: 18px;
        letter-spacing: 0.2px;
      }

      .content-head p {
        margin: 6px 0 0 0;
        color: var(--shell-muted);
        font-size: 13px;
        max-width: 72ch;
        line-height: 1.45;
      }

      .section {
        padding: 18px;
      }

      .panel {
        display: none;
      }

      .panel.active {
        display: block;
      }

      /* Cards inside panels */
      .grid {
        display: grid;
        grid-template-columns: repeat(12, 1fr);
        gap: 12px;
      }

      .card {
        grid-column: span 12;
        background: #ffffff;
        border: 1px solid #e5e7eb;
        border-radius: var(--shell-radius2);
        padding: 16px;
      }

      .card.subtle {
        background: #f9fafb;
      }

      .card h4 {
        margin: 0;
        font-size: 14px;
        letter-spacing: 0.2px;
      }

      .card p {
        margin: 6px 0 0 0;
        color: var(--shell-muted);
        font-size: 13px;
        line-height: 1.45;
      }

      .split {
        display: flex;
        gap: 12px;
        align-items: flex-start;
        justify-content: space-between;
        flex-wrap: wrap;
      }

      .row {
        display: flex;
        gap: 10px;
        align-items: center;
        flex-wrap: wrap;
        margin-top: 10px;
      }

      .status {
        display: inline-flex;
        align-items: center;
        gap: 8px;
        padding: 6px 10px;
        border-radius: 999px;
        border: 1px solid #e5e7eb;
        background: #ffffff;
        font-size: 12px;
        color: var(--shell-muted);
        font-weight: 650;
      }

      .status strong {
        color: var(--shell-text);
      }

      .badge {
        display: inline-flex;
        align-items: center;
        gap: 8px;
        padding: 6px 10px;
        border-radius: 999px;
        background: rgba(22, 163, 74, 0.10);
        border: 1px solid rgba(22, 163, 74, 0.25);
        color: #15803d;
        font-weight: 700;
        font-size: 12px;
      }

      .badge.warn {
        background: rgba(217, 119, 6, 0.10);
        border-color: rgba(217, 119, 6, 0.25);
        color: #b45309;
      }

      .badge.bad {
        background: rgba(220, 38, 38, 0.10);
        border-color: rgba(220, 38, 38, 0.25);
        color: #dc2626;
      }

      .hint {
        margin: 8px 0 0 0;
        font-size: 12px;
        color: var(--shell-muted2);
        line-height: 1.4;
      }

      .error-message {
        margin: 12px 0;
        padding: 12px 16px;
        background: rgba(220, 38, 38, 0.08);
        border: 1px solid rgba(220, 38, 38, 0.2);
        border-left: 3px solid #dc2626;
        border-radius: 6px;
        color: #991b1b;
        font-size: 13px;
        line-height: 1.5;
      }

      .error-message strong {
        color: #dc2626;
      }

      .url-row {
        display: flex;
        align-items: center;
        gap: 8px;
        margin-top: 6px;
      }

      .url-code {
        flex: 1;
        padding: 10px 12px;
        background: #f3f4f6;
        border: 1px solid #e5e7eb;
        border-radius: 6px;
        font-family: ui-monospace, SFMono-Regular, "SF Mono", Menlo, monospace;
        font-size: 13px;
        color: var(--shell-text);
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }

      /* Service selector tabs */
      .service-tabs {
        display: flex;
        gap: 4px;
        margin-bottom: 16px;
        border-bottom: 1px solid #e5e7eb;
        padding-bottom: 0;
      }

      .service-tab {
        display: flex;
        align-items: center;
        gap: 8px;
        padding: 10px 16px;
        border: none;
        background: transparent;
        color: var(--shell-muted);
        font-size: 14px;
        font-weight: 600;
        cursor: pointer;
        border-bottom: 2px solid transparent;
        margin-bottom: -1px;
        transition: color 0.15s ease, border-color 0.15s ease;
      }

      .service-tab:hover {
        color: var(--shell-text);
      }

      .service-tab.active {
        color: var(--shell-accent);
        border-bottom-color: var(--shell-accent);
      }

      .service-tab .service-status {
        width: 8px;
        height: 8px;
        border-radius: 50%;
        background: var(--shell-muted2);
      }

      .service-tab .service-status.good {
        background: var(--shell-good);
      }

      .service-tab .service-status.bad {
        background: var(--shell-bad);
      }

      /* Service content */
      .service-content {
        background: transparent;
        padding: 0;
      }

      .service-header {
        display: flex;
        align-items: flex-start;
        justify-content: space-between;
        gap: 12px;
        padding-bottom: 12px;
        margin-bottom: 12px;
        border-bottom: 1px solid #e5e7eb;
      }

      .service-header h5 {
        margin: 0;
        font-size: 16px;
        font-weight: 700;
      }

      .service-header .url {
        margin: 4px 0 0 0;
        color: var(--shell-muted);
        font-size: 13px;
        font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
      }

      /* Legacy service grid - keeping for reference */
      .service-grid {
        display: grid;
        grid-template-columns: repeat(2, minmax(0, 1fr));
        gap: 12px;
        margin-top: 10px;
      }

      .service {
        background: #ffffff;
        border: 1px solid #e5e7eb;
        border-radius: var(--shell-radius2);
        padding: 14px;
      }

      .service-top {
        display: flex;
        align-items: flex-start;
        justify-content: space-between;
        gap: 10px;
      }

      .service h5 {
        margin: 0;
        font-size: 14px;
      }

      .service .url {
        margin: 6px 0 0 0;
        color: var(--shell-muted);
        font-size: 12px;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
      }

      .tabs {
        display: flex;
        gap: 6px;
        margin-top: 12px;
        flex-wrap: wrap;
      }

      .tab {
        border: 1px solid #e5e7eb;
        background: #f9fafb;
        color: var(--shell-muted);
        padding: 7px 10px;
        border-radius: 999px;
        font-size: 12px;
        cursor: pointer;
        font-weight: 700;
      }

      .tab:hover {
        background: #f3f4f6;
      }

      .tab.active {
        background: rgba(124, 58, 237, 0.08);
        border-color: rgba(124, 58, 237, 0.25);
        color: var(--shell-accent);
      }

      .tab-pane {
        margin-top: 10px;
        border-top: 1px dashed #e5e7eb;
        padding-top: 10px;
        display: none;
      }

      .tab-pane.active {
        display: block;
      }

      .mono {
        font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
        font-size: 12px;
        color: var(--shell-text);
      }

      /* Token textarea */
      .token-area {
        width: 100%;
        min-height: 80px;
        padding: 10px 12px;
        border-radius: 12px;
        border: 1px solid #e5e7eb;
        background: #f9fafb;
        color: var(--shell-text);
        font-size: 12px;
        font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
        resize: vertical;
        outline: none;
      }

      .token-area:focus {
        box-shadow: var(--shell-focus);
        border-color: rgba(124, 58, 237, 0.4);
        background: #ffffff;
      }

      /* Toggle switch */
      .toggle-label {
        display: flex;
        align-items: center;
        gap: 12px;
        cursor: pointer;
        font-size: 14px;
        color: var(--shell-text);
      }

      .toggle-switch {
        position: relative;
        width: 44px;
        height: 24px;
        flex-shrink: 0;
      }

      .toggle-switch input {
        opacity: 0;
        width: 0;
        height: 0;
      }

      .toggle-slider {
        position: absolute;
        cursor: pointer;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background-color: #d1d5db;
        transition: 0.2s ease;
        border-radius: 24px;
      }

      .toggle-slider:before {
        position: absolute;
        content: "";
        height: 18px;
        width: 18px;
        left: 3px;
        bottom: 3px;
        background-color: white;
        transition: 0.2s ease;
        border-radius: 50%;
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
      }

      .toggle-switch input:checked + .toggle-slider {
        background-color: var(--shell-accent);
      }

      .toggle-switch input:checked + .toggle-slider:before {
        transform: translateX(20px);
      }

      .toggle-switch input:focus + .toggle-slider {
        box-shadow: var(--shell-focus);
      }

      .toggle-text {
        font-weight: 500;
      }

      /* Form fields */
      .field {
        display: flex;
        flex-direction: column;
        gap: 6px;
        margin-top: 10px;
      }

      .field label {
        font-size: 12px;
        color: var(--shell-muted);
        font-weight: 700;
      }

      .field input,
      .field select {
        width: 100%;
        padding: 10px 12px;
        border-radius: 12px;
        border: 1px solid #e5e7eb;
        background: #ffffff;
        color: var(--shell-text);
        font-size: 13px;
        outline: none;
        box-sizing: border-box;
      }

      .field input:focus,
      .field select:focus {
        box-shadow: var(--shell-focus);
        border-color: rgba(124, 58, 237, 0.4);
      }

      /* List items */
      .list {
        margin-top: 10px;
        display: flex;
        flex-direction: column;
        gap: 8px;
      }

      .item {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 10px;
        padding: 10px 10px;
        border-radius: 12px;
        border: 1px solid #e5e7eb;
        background: #ffffff;
      }

      .item:hover {
        background: #f9fafb;
      }

      .item .left {
        display: flex;
        align-items: center;
        gap: 10px;
        min-width: 0;
      }

      .item .title {
        font-weight: 750;
        font-size: 13px;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      .item .desc {
        margin-top: 2px;
        font-size: 12px;
        color: var(--shell-muted);
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      .item .right {
        display: flex;
        align-items: center;
        gap: 8px;
        flex-shrink: 0;
      }

      .icon {
        width: 34px;
        height: 34px;
        border-radius: 12px;
        display: grid;
        place-items: center;
        background: rgba(124, 58, 237, 0.10);
        border: 1px solid rgba(124, 58, 237, 0.20);
        color: var(--shell-accent);
        font-weight: 900;
      }

      .icon.gray {
        background: #f3f4f6;
        border-color: #e5e7eb;
        color: var(--shell-muted);
        font-weight: 800;
      }

      /* Table */
      table {
        width: 100%;
        border-collapse: collapse;
        margin-top: 10px;
        overflow: hidden;
        border-radius: 12px;
        border: 1px solid #e5e7eb;
        background: #ffffff;
      }

      th, td {
        padding: 10px 10px;
        border-bottom: 1px solid #e5e7eb;
        font-size: 13px;
        text-align: left;
      }

      th {
        color: var(--shell-muted);
        font-weight: 800;
        font-size: 12px;
        letter-spacing: 0.25px;
        text-transform: uppercase;
        background: #f9fafb;
      }

      tr:hover td {
        background: #f9fafb;
      }

      td .small {
        display: block;
        font-size: 12px;
        color: var(--shell-muted2);
        margin-top: 2px;
      }

      .right-align {
        text-align: right;
      }

      /* Toast */
      .toast {
        position: fixed;
        left: 50%;
        transform: translateX(-50%);
        bottom: 16px;
        background: #1f2937;
        border: 1px solid #374151;
        border-radius: 999px;
        padding: 10px 16px;
        color: #ffffff;
        font-size: 13px;
        display: none;
        gap: 10px;
        align-items: center;
        box-shadow: 0 4px 20px rgba(0, 0, 0, 0.25);
        z-index: 60;
      }

      .toast.show {
        display: flex;
      }

      /* Remove dialog styling override */
      .dialog-content {
        font-size: medium;
        color: var(--shell-text);
      }

      /* Hidden state for search filtering */
      [data-search-hidden="true"] {
        display: none !important;
      }

      /* Focus visible for buttons */
      .btn:focus-visible,
      .nav-btn:focus-visible,
      .tab:focus-visible {
        outline: 2px solid var(--shell-accent);
        outline-offset: 2px;
      }

      /* AWS Services Grid */
      .aws-services-grid {
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
        gap: 10px;
        margin-top: 12px;
      }

      .aws-service-link {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 6px;
        padding: 14px 10px;
        border-radius: 12px;
        border: 1px solid #e5e7eb;
        background: #ffffff;
        color: var(--shell-text);
        text-decoration: none;
        font-size: 12px;
        font-weight: 600;
        transition: background 0.15s ease, border-color 0.15s ease, transform 0.1s ease;
      }

      .aws-service-link:hover {
        background: #f9fafb;
        border-color: rgba(124, 58, 237, 0.3);
        transform: translateY(-1px);
      }

      .aws-service-link:focus {
        outline: none;
        box-shadow: var(--shell-focus);
      }

      .aws-service-icon {
        width: 32px;
        height: 32px;
        object-fit: contain;
      }

      .aws-service-name {
        text-align: center;
        line-height: 1.2;
      }

      /* Responsive */
      @media (max-width: 980px) {
        .shell-content {
          grid-template-columns: 1fr;
        }
        .sidebar {
          position: relative;
          top: auto;
          height: auto;
        }
        .search {
          width: 100%;
          max-width: none;
        }
        .service-grid {
          grid-template-columns: 1fr;
        }
      }
    `;
  }

  constructor() {
    super();
    this._activePanel = 'overview';
    this._healthStatus = {};
    this._deployments = [];
    this._systemTokens = {};
    this._activeServiceTab = { wave: 'status', fuss: 'status' };
    this._activeService = 'wave';
    this._toastMessage = '';
    this._toastVisible = false;
    this._searchQuery = '';
    this._searchFocused = false;
    this._searchDropdownOpen = false;
    this._isDemoResetEnv = false;
    this._advancedView = false;
    this._boundKeydownHandler = this._handleKeydown.bind(this);
  }

  connectedCallback() {
    super.connectedCallback();
    this.client = this.requestInstance('d2l-pm-client');
    this._initFromHash();
    window.addEventListener('hashchange', this._onHashChange.bind(this));
    document.addEventListener('keydown', this._boundKeydownHandler);
    this._fetchData();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('hashchange', this._onHashChange.bind(this));
    document.removeEventListener('keydown', this._boundKeydownHandler);
  }

  _handleKeydown(e) {
    const mod = e.metaKey || e.ctrlKey;

    // Cmd/Ctrl+K - Focus search
    if (mod && e.key.toLowerCase() === 'k') {
      e.preventDefault();
      this._focusSearch();
    }

    // Escape - Close search dropdown and blur
    if (e.key === 'Escape' && (this._searchFocused || this._searchDropdownOpen)) {
      e.preventDefault();
      this._searchDropdownOpen = false;
      this._searchQuery = '';
      const input = this.shadowRoot.getElementById('global-search');
      if (input) input.blur();
    }
  }

  _focusSearch() {
    const input = this.shadowRoot.getElementById('global-search');
    if (input) {
      input.focus();
      input.select();
    }
  }

  /**
   * Build the Wave API URL for an environment.
   * Matches the logic from api/application/directus-sync.js
   * @param {string} environmentId - The environment ID (e.g., 'stable', 'qa', 'dev')
   * @returns {string} The Wave API URL
   */
  _buildWaveApiUrl(environmentId) {
    // Get baseUrl from session config (from stage-config.js)
    const baseUrl = this.client?.session?.baseUrl || 'skillswave.dev'; // fallback to default
    // Get region from environment, defaulting to 'us-east-1'
    const region = this.environment?.region || 'us-east-1';

    const stage = environmentId === 'stable' ? '' : `.${environmentId}`;
    return `https://${region}.api${stage}.${baseUrl}/api`;
  }

  /**
   * Build the FUSS API URL for an environment.
   * Format: https://{region}.fuss.api.{baseurl}
   * @param {string} environmentId - The environment ID (e.g., 'stable', 'qa', 'dev')
   * @returns {string} The FUSS API URL
   */
  _buildFussApiUrl(environmentId) {
    const baseUrl = this.client?.session?.baseUrl || 'skillswave.dev';
    const fussRegion = this.environment?.fussRegion || this.environment?.region || 'us-east-1';
    const stage = environmentId === 'stable' ? '' : `.${environmentId}`;
    return `https://${fussRegion}.fuss.api${stage}.${baseUrl}`;
  }

  _initFromHash() {
    const hash = window.location.hash.replace('#', '') || 'overview';
    const validPanels = ['overview', 'tenants', 'services', 'integrations', 'aws', 'settings', 'tasks'];
    this._activePanel = validPanels.includes(hash) ? hash : 'overview';
  }

  _onHashChange() {
    this._initFromHash();
  }

  async _fetchData() {
    if (!this.environmentId) return;

    try {
      this._healthStatus = await this.client.fetchEnvironmentHealth(this.environmentId);
      this._isDemoResetEnv = this._healthStatus?.waveHealth?.tags?.includes('DEMO-RESET') || false;
      if (this._isDemoResetEnv && !this._advancedView && !window.location.hash) {
        this._activePanel = 'tenants';
      }
    } catch (error) {
      console.error('Error fetching health:', error);
    }

    try {
      this._deployments = await this.client.fetchDeployments(this.environmentId);
    } catch (error) {
      console.error('Error fetching deployments:', error);
    }
  }

  async _refresh() {
    this._toast('Refreshing...');
    try {
      const [, refreshResult] = await Promise.all([
        this._fetchData(),
        this.client.refreshEnvironmentStatus(this.environmentId),
      ]);
      this.environment = { ...this.environment, ...refreshResult };
      this._toast('Refreshed');
    } catch (error) {
      console.error('Error refreshing:', error);
      this._toast('Refresh failed');
    }
  }

  async _copyLink() {
    const url = `${window.location.origin}${window.location.pathname}#${this._activePanel}`;
    await navigator.clipboard.writeText(url);
    this._toast('Copied link');
  }

  _setPanel(panel) {
    this._activePanel = panel;
    // Preserve the current path, only update the hash
    const url = new URL(window.location.href);
    url.hash = panel;
    history.replaceState(null, '', url.toString());
  }

  _toast(message) {
    this._toastMessage = message;
    this._toastVisible = true;
    clearTimeout(this._toastTimer);
    this._toastTimer = setTimeout(() => {
      this._toastVisible = false;
    }, 1800);
  }

  async _copyAndToast(value, message) {
    await navigator.clipboard.writeText(value);
    this._toast(message);
  }

  _getOverallHealth() {
    const waveOk = this._healthStatus?.waveHealth?.status === 'ok';
    const fussOk = this._healthStatus?.fussHealth?.status === 'ok';

    if (waveOk && fussOk) return 'good';
    if (waveOk || fussOk) return 'warn';
    if (this._healthStatus?.waveHealth?.status === 'error' || this._healthStatus?.fussHealth?.status === 'error') return 'bad';
    return 'muted';
  }

  _getHealthLabel() {
    const status = this._getOverallHealth();
    if (status === 'good') return 'Healthy';
    if (status === 'warn') return 'Degraded';
    if (status === 'bad') return 'Error';
    return 'Checking...';
  }

  _handleRunTask() {
    this._setPanel('tasks');
    this._toast('Pick a task to run');
  }

  async _generateToken(system) {
    try {
      const { token } = await this.client.fetchToken(this.environmentId, system);
      this._systemTokens = {
        ...this._systemTokens,
        [system]: token,
      };
      this._toast(`Generated ${system.toUpperCase()} token`);
    } catch (error) {
      console.error(`Error generating ${system} token:`, error);
      this._toast(`Failed to generate ${system} token`);
    }
  }

  _setServiceTab(service, tab) {
    this._activeServiceTab = {
      ...this._activeServiceTab,
      [service]: tab,
    };
  }

  // Event handlers that use data attributes (to avoid arrow functions in templates)
  _handlePanelClick(e) {
    const panel = e.currentTarget.dataset.panel;
    if (panel) this._setPanel(panel);
  }

  _handleServiceClick(e) {
    const serviceId = e.currentTarget.dataset.service;
    if (serviceId) this._activeService = serviceId;
  }

  _handleServiceTabClick(e) {
    const { service, tab } = e.currentTarget.dataset;
    if (service && tab) this._setServiceTab(service, tab);
  }

  _handleGenerateToken(e) {
    const system = e.currentTarget.dataset.system;
    if (system) this._generateToken(system);
  }

  _handleCopyToken(e) {
    const system = e.currentTarget.dataset.system;
    if (system) this._copyToken(system);
  }

  _handleCopyTokenAsHeader(e) {
    const system = e.currentTarget.dataset.system;
    if (system) this._copyTokenAsHeader(system);
  }

  _handleCopyClick(e) {
    const { copyValue, copyMessage } = e.currentTarget.dataset;
    if (copyValue) this._copyAndToast(copyValue, copyMessage || 'Copied');
  }

  _handleOpenUrl(e) {
    const url = e.currentTarget.dataset.url;
    if (url) window.open(url, '_blank');
  }

  _handleSearchResultClick(e) {
    const { group, index } = e.currentTarget.dataset;
    const results = this._getSearchResults();
    const item = results[group]?.[parseInt(index, 10)];
    if (item) this._selectSearchResult(item);
  }

  _handleCopyConfig() {
    this._toast('Copied current config');
  }

  _handleSearchInput(e) {
    this._searchQuery = e.target.value.toLowerCase();
    this._searchDropdownOpen = this._searchQuery.length > 0 || this._searchFocused;
  }

  _handleSearchFocus() {
    this._searchFocused = true;
    this._searchDropdownOpen = true;
  }

  _handleSearchBlur() {
    this._searchFocused = false;
    // Delay closing to allow click on results
    setTimeout(() => {
      if (!this._searchFocused) {
        this._searchDropdownOpen = false;
      }
    }, 150);
  }

  _getSearchResults() {
    const query = this._searchQuery.toLowerCase();
    const results = { tenants: [], services: [], settings: [] };

    // Tenant items from environment data
    const tenants = this.environment?.tenants || [];
    const tenantItems = tenants.map(t => ({
      title: t.displayName || t.id,
      subtitle: `${t.type || 'Tenant'} · ${t.id}`,
      icon: t.type === 'provider' ? '🏫' : t.type === 'employer' ? '🏢' : '👤',
      action: () => {
        // Navigate to tenant page: /tenants/{environmentId}/{tenantId}
        // Use standard href navigation to ensure proper browser history
        let url = `/tenants/${this.environmentId}/${t.id}`;
        if (this.environment?.fussUrl) url = `${url}?fuss=yes`;
        window.location.href = url;
      },
    }));

    // Service items
    const serviceItems = [
      { title: 'Wave', subtitle: 'Core learning platform API', icon: '🌊', action: () => { this._setPanel('services'); this._activeService = 'wave'; } },
      { title: 'FUSS', subtitle: 'Federated User Storage', icon: '🔐', action: () => { this._setPanel('services'); this._activeService = 'fuss'; } },
      { title: 'Generate Wave token', subtitle: 'OAuth bearer token', icon: '🔑', action: () => { this._setPanel('services'); this._activeService = 'wave'; this._setServiceTab('wave', 'token'); } },
      { title: 'Generate FUSS token', subtitle: 'OAuth bearer token', icon: '🔑', action: () => { this._setPanel('services'); this._activeService = 'fuss'; this._setServiceTab('fuss', 'token'); } },
    ];

    // Settings items - copy value on click
    const region = this.environment?.region || 'us-east-1';
    const waveUrl = this._buildWaveApiUrl(this.environmentId);
    const fussUrl = this.environment?.fussUrl || this._buildFussApiUrl(this.environmentId);
    const amplitudeId = this.environment?.amplitudeProjectId;

    const settingsItems = [
      { title: 'Wave URL', subtitle: waveUrl, icon: '📋', copyable: true, action: () => this._copyAndToast(waveUrl, 'Copied Wave URL') },
      { title: 'FUSS URL', subtitle: fussUrl, icon: '📋', copyable: true, action: () => this._copyAndToast(fussUrl, 'Copied FUSS URL') },
      ...(amplitudeId ? [{ title: 'Amplitude Project ID', subtitle: amplitudeId, icon: '📋', copyable: true, action: () => this._copyAndToast(amplitudeId, 'Copied Amplitude ID') }] : []),
      { title: 'Region', subtitle: region, icon: '📋', copyable: true, action: () => this._copyAndToast(region, 'Copied region') },
    ];

    // Filter by query
    const matchesQuery = (item) => {
      if (!query) return true;
      return item.title.toLowerCase().includes(query) ||
        item.subtitle.toLowerCase().includes(query);
    };

    results.tenants = tenantItems.filter(matchesQuery).slice(0, 8);
    results.services = serviceItems.filter(matchesQuery).slice(0, 4);
    results.settings = settingsItems.filter(matchesQuery).slice(0, 4);

    return results;
  }

  _selectSearchResult(item) {
    this._searchDropdownOpen = false;
    this._searchQuery = '';
    if (item.action) {
      item.action();
    }
  }

  async _copyToken(system) {
    const token = this._systemTokens[system]?.access_token || '';
    if (token) {
      await navigator.clipboard.writeText(token);
      this._toast('Copied token');
    }
  }

  async _copyTokenAsHeader(system) {
    const token = this._systemTokens[system]?.access_token || '';
    if (token) {
      await navigator.clipboard.writeText(`Authorization: Bearer ${token}`);
      this._toast('Copied as Authorization header');
    }
  }

  async _saveAmplitudeConfig(e) {
    e.preventDefault();
    const form = this.shadowRoot.getElementById('amplitude-form');
    const amplitudeProjectId = form.querySelector('#amplitudeProjectId').value;

    try {
      await this.client.updateEnvironmentConfig(this.environmentId, { amplitudeProjectId });
      this.environment = { ...this.environment, amplitudeProjectId };
      this._toast('Amplitude config saved');
    } catch (error) {
      console.error('Error saving config:', error);
      this._toast('Failed to save config');
    }
  }

  async _saveEnvironmentConfig(e) {
    e.preventDefault();
    const form = this.shadowRoot.getElementById('environment-config-form');
    const region = form.querySelector('#region').value;
    const fussRegion = form.querySelector('#fussRegion').value;
    const waveUrlInput = form.querySelector('#waveUrl').value.trim();
    const waveUrl = waveUrlInput || null;

    try {
      await this.client.updateEnvironmentConfig(this.environmentId, { region, fussRegion, waveUrl });
      this.environment = { ...this.environment, region, fussRegion, waveUrl };
      this._toast('Environment config saved');
    } catch (error) {
      console.error('Error saving config:', error);
      this._toast('Failed to save config');
    }
  }

  async _toggleDirectusSync(e) {
    const directusSyncEnabled = e.target.checked;

    try {
      await this.client.updateDirectusSyncConfig(this.environmentId, { directusSyncEnabled });
      this.environment = { ...this.environment, directusSyncEnabled };
      this._toast(directusSyncEnabled ? 'Directus sync enabled' : 'Directus sync disabled');
    } catch (error) {
      console.error('Error updating Directus sync setting:', error);
      // Revert the checkbox
      e.target.checked = !directusSyncEnabled;
      this._toast('Failed to update Directus sync setting');
    }
  }

  // Header template
  _renderHeader() {
    const healthClass = this._getOverallHealth();
    const searchResults = this._getSearchResults();
    const hasResults = searchResults.tenants.length || searchResults.services.length || searchResults.settings.length;

    return html`
      <div class="shell-header">
        <div class="header-inner">
          <div class="env-info">
            <h1 class="env-title-main">${this.environment?.displayName || this.environmentId}</h1>
            <div class="env-badges">
              <span class="pill"><span class="dot ${healthClass}"></span>${this._getHealthLabel()}</span>
              <span class="pill muted">Stage: <strong>${this.environmentId}</strong></span>
            </div>
          </div>

          <div class="header-actions">
            <div class="header-search">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path d="M21 21l-4.3-4.3m1.8-5.2a7 7 0 11-14 0 7 7 0 0114 0z" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
              </svg>
              <input
                id="global-search"
                type="text"
                placeholder="Search..."
                .value="${this._searchQuery}"
                @input="${this._handleSearchInput}"
                @focus="${this._handleSearchFocus}"
                @blur="${this._handleSearchBlur}"
                aria-label="Search environment content"
                autocomplete="off"
              />
              <span class="search-shortcut"><span class="kbd">⌘</span><span class="kbd">K</span></span>

              <div class="search-dropdown ${this._searchDropdownOpen ? 'open' : ''}">
                ${hasResults ? html`
                  ${searchResults.tenants.length ? html`
                    <div class="search-group tenants">
                      <div class="search-group-label">Tenants</div>
                      ${searchResults.tenants.map((item, idx) => html`
                        <div class="search-result" data-group="tenants" data-index="${idx}" @mousedown="${this._handleSearchResultClick}">
                          <span class="result-icon">${item.icon}</span>
                          <div class="result-text">
                            <div class="result-title">${item.title}</div>
                            <div class="result-subtitle">${item.subtitle}</div>
                          </div>
                        </div>
                      `)}
                    </div>
                  ` : ''}

                  ${searchResults.services.length ? html`
                    <div class="search-group services">
                      <div class="search-group-label">Services</div>
                      ${searchResults.services.map((item, idx) => html`
                        <div class="search-result" data-group="services" data-index="${idx}" @mousedown="${this._handleSearchResultClick}">
                          <span class="result-icon">${item.icon}</span>
                          <div class="result-text">
                            <div class="result-title">${item.title}</div>
                            <div class="result-subtitle">${item.subtitle}</div>
                          </div>
                        </div>
                      `)}
                    </div>
                  ` : ''}

                  ${searchResults.settings.length ? html`
                    <div class="search-group settings">
                      <div class="search-group-label">Copy to Clipboard</div>
                      ${searchResults.settings.map((item, idx) => html`
                        <div class="search-result copyable" data-group="settings" data-index="${idx}" @mousedown="${this._handleSearchResultClick}">
                          <span class="result-icon">${item.icon}</span>
                          <div class="result-text">
                            <div class="result-title">${item.title}</div>
                            <div class="result-subtitle">${item.subtitle}</div>
                          </div>
                          <span class="copy-hint">Click to copy</span>
                        </div>
                      `)}
                    </div>
                  ` : ''}
                ` : html`
                  <div class="search-empty">No results found for "${this._searchQuery}"</div>
                `}
              </div>
            </div>
            ${this._isDemoResetEnv ? html`
            <button class="btn" @click="${this._toggleAdvancedView}">
              <span>${this._advancedView ? 'Simple View' : 'Advanced View'}</span>
            </button>
            ` : ''}
            <button class="btn" @click="${this._refresh}">
              <span>Refresh</span>
            </button>
            <button class="btn" @click="${this._openRemoveDialog}">
              <span>Remove</span>
            </button>
          </div>
        </div>
      </div>
    `;
  }

  _toggleAdvancedView() {
    this._advancedView = !this._advancedView;
    if (!this._advancedView) {
      this._activePanel = 'tenants';
    }
  }

  _openRemoveDialog() {
    const dialog = this.shadowRoot.getElementById('remove-environment-dialog');
    if (dialog) {
      dialog.opened = true;
    }
  }

  _closeRemoveDialog() {
    const dialog = this.shadowRoot.getElementById('remove-environment-dialog');
    if (dialog) {
      dialog.opened = false;
    }
  }

  async _handleRemoveClick() {
    try {
      await this.client.removeEnvironment(this.environmentId);
      this._closeRemoveDialog();
      window.location.href = '/';
    } catch {
      // Error alert is handled by the client
    }
  }

  // Sidebar template
  _renderSidebar() {
    const waveHealth = this._healthStatus?.waveHealth?.status === 'ok' ? 'good' : this._healthStatus?.waveHealth?.status === 'error' ? 'bad' : '';
    const fussHealth = this._healthStatus?.fussHealth?.status === 'ok' ? 'good' : this._healthStatus?.fussHealth?.status === 'error' ? 'bad' : '';
    const hasAmplitude = !!this.environment?.amplitudeProjectId;

    const hasAwsAccount = !!this.environment?.awsAccountNumber;

    const navItems = [
      { id: 'overview', label: 'Overview', meta: 'Summary' },
      { id: 'tenants', label: 'Tenants', meta: this.environment?.tenants?.length || 0 },
      { id: 'services', label: 'Services', meta: '2' },
      { id: 'integrations', label: 'Integrations', meta: '1' },
      { id: 'aws', label: 'AWS', meta: hasAwsAccount ? '8 links' : 'Setup' },
      { id: 'settings', label: 'Settings', meta: 'Read-only' },
      { id: 'tasks', label: 'Tasks', meta: 'Recent' },
    ];

    return html`
      <aside class="sidebar">
        <div class="side-card">
          <div class="side-top">
            <p class="side-label">Services</p>
            <div class="stack">
              <span class="chip"><span class="dot ${waveHealth}"></span>Wave</span>
              <span class="chip"><span class="dot ${fussHealth}"></span>FUSS</span>
              <span class="chip"><span class="dot ${hasAmplitude ? 'warn' : ''}"></span>Amplitude</span>
              ${this.hasTaskManagement ? html`<span class="chip"><span class="dot good"></span>Tasks</span>` : ''}
            </div>
          </div>

          <nav class="nav" aria-label="Environment sections">
            ${navItems.map(item => html`
              <button
                class="nav-btn ${this._activePanel === item.id ? 'active' : ''}"
                data-panel="${item.id}"
                @click="${this._handlePanelClick}">
                <span>${item.label}</span>
                <span class="nav-meta">${item.meta}</span>
              </button>
            `)}
          </nav>
        </div>
      </aside>
    `;
  }

  // Panel title/description data
  _getPanelMeta() {
    const meta = {
      overview: {
        title: 'Overview',
        desc: 'Environment health, recent deployments, and quick actions.',
      },
      tenants: {
        title: 'Tenants',
        desc: 'Browse and manage all tenants in this environment.',
      },
      services: {
        title: 'Services',
        desc: 'Service health, authentication tokens, API documentation, and configuration.',
      },
      integrations: {
        title: 'Integrations',
        desc: 'Third-party service connections and their current status.',
      },
      aws: {
        title: 'AWS',
        desc: 'Quick links to AWS console services for this stage.',
      },
      settings: {
        title: 'Settings',
        desc: 'Environment configuration and feature flags.',
      },
      tasks: {
        title: 'Tasks',
        desc: 'Recent task runs, filtering, and drill-in details.',
      },
    };
    return meta[this._activePanel] || meta.overview;
  }

  // Main content template
  _renderMainContent() {
    const panelMeta = this._getPanelMeta();

    return html`
      <main class="main-content">
        <div class="content-card" role="region" aria-label="Environment content">
          <div class="content-head">
            <div>
              <h3>${panelMeta.title}</h3>
              <p>${panelMeta.desc}</p>
            </div>
          </div>

          <section class="section panel ${this._activePanel === 'overview' ? 'active' : ''}" data-panel="overview">
            ${this._renderOverviewPanel()}
          </section>

          <section class="section panel ${this._activePanel === 'tenants' ? 'active' : ''}" data-panel="tenants">
            ${this._renderTenantsPanel()}
          </section>

          <section class="section panel ${this._activePanel === 'services' ? 'active' : ''}" data-panel="services">
            ${this._renderServicesPanel()}
          </section>

          <section class="section panel ${this._activePanel === 'integrations' ? 'active' : ''}" data-panel="integrations">
            ${this._renderIntegrationsPanel()}
          </section>

          <section class="section panel ${this._activePanel === 'aws' ? 'active' : ''}" data-panel="aws">
            ${this._renderAwsPanel()}
          </section>

          <section class="section panel ${this._activePanel === 'settings' ? 'active' : ''}" data-panel="settings">
            ${this._renderSettingsPanel()}
          </section>

          <section class="section panel ${this._activePanel === 'tasks' ? 'active' : ''}" data-panel="tasks">
            ${this._renderTasksPanel()}
          </section>
        </div>
      </main>
    `;
  }

  // Overview panel
  _renderOverviewPanel() {
    const waveOk = this._healthStatus?.waveHealth?.status === 'ok';
    const fussOk = this._healthStatus?.fussHealth?.status === 'ok';
    const allHealthy = waveOk && fussOk;
    const recentDeployments = (this._deployments || []).slice(0, 2);

    return html`
      <div class="grid">
        <div class="card subtle" style="grid-column: span 7;">
          <div class="split">
            <div>
              <h4>At a glance</h4>
              <p>Overview of services and integrations in this environment.</p>
            </div>
            <div class="badge ${allHealthy ? '' : 'warn'}">
              <span class="dot ${allHealthy ? 'good' : 'warn'}"></span>
              ${allHealthy ? 'All services healthy' : 'Some services need attention'}
            </div>
          </div>

          <div class="row">
            <span class="status">
              <span class="dot ${waveOk ? 'good' : 'bad'}"></span>
              <strong>Wave</strong> OAuth tokens · health · docs · settings
            </span>
            <span class="status">
              <span class="dot ${fussOk ? 'good' : 'bad'}"></span>
              <strong>FUSS</strong> OAuth tokens · health · docs · settings
            </span>
          </div>

          <div class="row">
            <span class="status">
              <span class="dot ${this.environment?.amplitudeProjectId ? 'warn' : ''}"></span>
              <strong>Amplitude</strong> linked project for this stage
            </span>
            ${this.hasTaskManagement ? html`
              <span class="status">
                <span class="dot good"></span>
                <strong>Tasks</strong> history + runs
              </span>
            ` : ''}
          </div>
        </div>

        <div class="card subtle" style="grid-column: span 5;">
          <h4>Quick actions</h4>
          <p>Common tasks for this environment.</p>
          <div class="row">
            <button class="btn primary" data-panel="services" @click="${this._handlePanelClick}">Generate token</button>
            <button class="btn" data-panel="tenants" @click="${this._handlePanelClick}">Open tenant</button>
          </div>
          <div class="row">
            <button class="btn" data-panel="tasks" @click="${this._handlePanelClick}">View tasks</button>
          </div>
        </div>

        <div class="card subtle" style="grid-column: span 12;">
          <div class="split">
            <div>
              <h4>Recent activity</h4>
              <p>Recent deployments and configuration changes.</p>
            </div>
            <button class="btn" data-url="/deployments/${this.environmentId}" @click="${this._handleOpenUrl}">View all</button>
          </div>
          <div class="list">
            ${recentDeployments.length === 0 ? html`
              <div class="item">
                <div class="left">
                  <div class="icon gray">—</div>
                  <div>
                    <div class="title">No recent deployments</div>
                    <div class="desc">Deploy to see activity here</div>
                  </div>
                </div>
              </div>
            ` : recentDeployments.map(deploy => html`
              <div class="item" data-search-text="deployment ${deploy.branchName} ${deploy.run_number}">
                <div class="left">
                  <div class="icon gray">⇄</div>
                  <div style="min-width:0">
                    <div class="title">Deployment #${deploy.run_number}</div>
                    <div class="desc">${deploy.branchName} · ${new Date(deploy.started_on).toLocaleDateString()}</div>
                  </div>
                </div>
                <div class="right">
                  <span class="badge"><span class="dot good"></span>Completed</span>
                  <button class="btn" data-url="${deploy.html_url}" @click="${this._handleOpenUrl}">Open</button>
                </div>
              </div>
            `)}
          </div>
        </div>
      </div>
    `;
  }

  // Tenants panel
  _renderTenantsPanel() {
    return html`
      <pm-environment environment-id="${this.environmentId}" .environment="${this.environment}" ?is-demo-reset="${this._isDemoResetEnv}"></pm-environment>
    `;
  }

  // Services panel
  _renderServicesPanel() {
    const services = this._getServicesConfig();

    return html`
      <!-- Service selector tabs -->
      <div class="service-tabs" role="tablist" aria-label="Select service">
        ${services.map(svc => html`
          <button
            class="service-tab ${this._activeService === svc.id ? 'active' : ''}"
            role="tab"
            aria-selected="${this._activeService === svc.id}"
            data-service="${svc.id}"
            @click="${this._handleServiceClick}"
          >
            <span class="service-status ${svc.healthStatus}"></span>
            ${svc.name}
          </button>
        `)}
      </div>

      <!-- Active service content -->
      ${services.map(svc => (this._activeService === svc.id ? this._renderServiceContent(svc) : ''))}
    `;
  }

  _getServicesConfig() {
    const waveHealth = this._healthStatus?.waveHealth;
    const fussHealth = this._healthStatus?.fussHealth;
    const waveUrl = this._buildWaveApiUrl(this.environmentId);
    const fussUrl = this._buildFussApiUrl(this.environmentId);

    return [
      {
        id: 'wave',
        name: 'Wave',
        url: waveUrl,
        health: waveHealth,
        healthStatus: waveHealth?.status === 'ok' ? 'good' : waveHealth?.status === 'error' ? 'bad' : '',
        healthLabel: waveHealth?.status === 'ok' ? 'Healthy' : 'Error',
        errorMessage: waveHealth?.message || waveHealth?.error || null,
        settings: waveHealth?.settings,
        description: 'Core learning experience platform API',
      },
      {
        id: 'fuss',
        name: 'FUSS',
        url: fussUrl,
        health: fussHealth,
        healthStatus: fussHealth?.status === 'ok' ? 'good' : fussHealth?.status === 'error' ? 'bad' : '',
        healthLabel: fussHealth?.status === 'ok' ? 'Healthy' : 'Error',
        errorMessage: fussHealth?.message || fussHealth?.error || null,
        settings: null,
        description: 'Federated User Storage Service',
      },
      // Future services can be added here:
      // { id: 'braze', name: 'Braze', url: '...', health: null, ... },
      // { id: 'amplitude', name: 'Amplitude', url: '...', health: null, ... },
    ];
  }

  _renderServiceContent(svc) {
    const activeTab = this._activeServiceTab[svc.id] || 'status';

    return html`
      <div class="service-content" data-search-text="${svc.id} service healthy token docs settings oauth">
        <div class="service-header">
          <div>
            <h5>${svc.name}</h5>
            <div class="url">${svc.url}</div>
          </div>
          <span class="badge ${svc.health?.status === 'ok' ? '' : 'bad'}">
            <span class="dot ${svc.healthStatus}"></span>
            ${svc.healthLabel}
          </span>
        </div>

        <div class="tabs" role="tablist" aria-label="${svc.name} options">
          <button class="tab ${activeTab === 'status' ? 'active' : ''}" data-service="${svc.id}" data-tab="status" @click="${this._handleServiceTabClick}">Status</button>
          <button class="tab ${activeTab === 'token' ? 'active' : ''}" data-service="${svc.id}" data-tab="token" @click="${this._handleServiceTabClick}">Token</button>
          <button class="tab ${activeTab === 'docs' ? 'active' : ''}" data-service="${svc.id}" data-tab="docs" @click="${this._handleServiceTabClick}">API</button>
          <button class="tab ${activeTab === 'settings' ? 'active' : ''}" data-service="${svc.id}" data-tab="settings" @click="${this._handleServiceTabClick}">Settings</button>
        </div>

        <!-- Status tab -->
        <div class="tab-pane ${activeTab === 'status' ? 'active' : ''}">
          <div class="row">
            <span class="status">
              <span class="dot ${svc.healthStatus}"></span>
              Status <strong>${svc.health?.status || 'Unknown'}</strong>
            </span>
          </div>
          ${svc.errorMessage ? html`
            <div class="error-message">
              <strong>Error:</strong> ${svc.errorMessage}
            </div>
          ` : ''}
          <p class="hint">${svc.description}</p>
        </div>

        <!-- Token tab -->
        <div class="tab-pane ${activeTab === 'token' ? 'active' : ''}">
          <p class="hint">Generate an OAuth bearer token for ${svc.name} to make API calls.</p>
          <div class="row">
            <button class="btn primary" data-system="${svc.id}" @click="${this._handleGenerateToken}">Generate</button>
          </div>
          ${this._systemTokens[svc.id] ? html`
            <div class="field">
              <label>Bearer token</label>
              <textarea class="token-area mono" readonly>${this._systemTokens[svc.id].access_token || ''}</textarea>
              <div class="row" style="margin-top:6px">
                <button class="btn" data-system="${svc.id}" @click="${this._handleCopyToken}">Copy</button>
                <button class="btn" data-system="${svc.id}" @click="${this._handleCopyTokenAsHeader}">Copy as header</button>
              </div>
            </div>
            <jwt-decoder .jwt="${this._systemTokens[svc.id].access_token}"></jwt-decoder>
          ` : ''}
        </div>

        <!-- Docs tab -->
        <div class="tab-pane ${activeTab === 'docs' ? 'active' : ''}">
          ${svc.id === 'wave' ? html`
            <p class="hint">Explore and test ${svc.name} API endpoints.</p>
            <div class="field">
              <label>API</label>
              <div class="url-row">
                <code class="url-code">https://docs.${this.environmentId}.skillswave.dev/</code>
                <button class="btn" data-copy-value="https://docs.${this.environmentId}.skillswave.dev/" data-copy-message="Copied docs URL" @click="${this._handleCopyClick}">Copy</button>
                <a href="https://docs.${this.environmentId}.skillswave.dev/" target="_blank" rel="noopener" class="btn">Open</a>
              </div>
            </div>
            <div class="field">
              <label>OpenAPI JSON</label>
              <div class="url-row">
                <code class="url-code">https://docs.${this.environmentId}.skillswave.dev/openapi.json</code>
                <button class="btn" data-copy-value="https://docs.${this.environmentId}.skillswave.dev/openapi.json" data-copy-message="Copied OpenAPI URL" @click="${this._handleCopyClick}">Copy</button>
                <a href="https://docs.${this.environmentId}.skillswave.dev/openapi.json" target="_blank" rel="noopener" class="btn">Open</a>
              </div>
            </div>
          ` : svc.id === 'fuss' ? html`
            <p class="hint">Explore and test ${svc.name} API endpoints.</p>
            <div class="field">
              <label>API</label>
              <div class="url-row">
                <code class="url-code">https://docs.fuss.${this.environmentId}.skillswave.dev/</code>
                <button class="btn" data-copy-value="https://docs.fuss.${this.environmentId}.skillswave.dev/" data-copy-message="Copied docs URL" @click="${this._handleCopyClick}">Copy</button>
                <a href="https://docs.fuss.${this.environmentId}.skillswave.dev/" target="_blank" rel="noopener" class="btn">Open</a>
              </div>
            </div>
            <div class="field">
              <label>OpenAPI JSON</label>
              <div class="url-row">
                <code class="url-code">https://docs.fuss.${this.environmentId}.skillswave.dev/openapi.json</code>
                <button class="btn" data-copy-value="https://docs.fuss.${this.environmentId}.skillswave.dev/openapi.json" data-copy-message="Copied OpenAPI URL" @click="${this._handleCopyClick}">Copy</button>
                <a href="https://docs.fuss.${this.environmentId}.skillswave.dev/openapi.json" target="_blank" rel="noopener" class="btn">Open</a>
              </div>
            </div>
          ` : html`
            <p class="hint">API documentation not available for ${svc.name}.</p>
          `}
        </div>

        <!-- Settings tab -->
        <div class="tab-pane ${activeTab === 'settings' ? 'active' : ''}">
          ${svc.settings ? html`
            <p class="hint">${svc.name} environment settings (read-only).</p>
            ${svc.id === 'wave' && svc.settings ? html`
              <div class="field">
                <label>Email enabled</label>
                <input type="text" readonly .value="${svc.settings.emailEnabled ? 'Yes' : 'No'}" />
              </div>
              <div class="field">
                <label>Bypass magic link tenants</label>
                <input type="text" readonly .value="${svc.settings.bypassLoginLinkTenants?.join(', ') || 'None'}" />
              </div>
            ` : ''}
          ` : html`
            <p class="hint">No settings available for ${svc.name}.</p>
            <div class="row">
              <button class="btn">View config</button>
            </div>
          `}
        </div>
      </div>
    `;
  }

  // Integrations panel
  _renderIntegrationsPanel() {
    const amplitudeProjectId = this.environment?.amplitudeProjectId || '';
    const directusSyncEnabled = this.environment?.directusSyncEnabled ?? true; // Default to enabled

    return html`
      <div class="grid">
        <!-- Directus Sync Card -->
        <div class="card subtle" style="grid-column: span 12;">
          <div class="split">
            <div>
              <h4>Directus Sync</h4>
              <p class="hint">When enabled, this environment will be synced to Directus as a Wave instance. Disable this for local development environments that shouldn't appear in the catalog.</p>
            </div>
            <span class="badge ${directusSyncEnabled ? '' : 'warn'}">
              <span class="dot ${directusSyncEnabled ? 'good' : 'warn'}"></span>
              ${directusSyncEnabled ? 'Enabled' : 'Disabled'}
            </span>
          </div>

          <div class="row">
            <label class="toggle-label">
              <span class="toggle-switch">
                <input
                  type="checkbox"
                  id="directusSyncEnabled"
                  .checked="${directusSyncEnabled}"
                  @change="${this._toggleDirectusSync}"
                />
                <span class="toggle-slider"></span>
              </span>
              <span class="toggle-text">Sync this environment to Directus</span>
            </label>
          </div>
        </div>

        <div class="card subtle" style="grid-column: span 7;">
          <div class="split">
            <div>
              <h4>Amplitude</h4>
              <p class="hint">Configure which Amplitude project is linked for this stage.</p>
            </div>
            <span class="badge ${amplitudeProjectId ? '' : 'warn'}">
              <span class="dot ${amplitudeProjectId ? 'good' : 'warn'}"></span>
              ${amplitudeProjectId ? 'Configured' : 'Not configured'}
            </span>
          </div>

          <form id="amplitude-form" @submit="${this._saveAmplitudeConfig}">
            <div class="field">
              <label for="amplitudeProjectId">Amplitude Project ID</label>
              <input type="text" id="amplitudeProjectId" placeholder="e.g., 123456" .value="${amplitudeProjectId}" />
            </div>

            <div class="row">
              <button type="submit" class="btn primary">Save</button>
              <button type="button" class="btn" @click="${this._handleCopyConfig}">Copy config</button>
            </div>
          </form>
        </div>

        <div class="card subtle" style="grid-column: span 5;">
          <h4>About this integration</h4>
          <p class="hint">This does not control where events are sent. It only changes which Amplitude project is linked from Kraken for this stage.</p>
          <div class="list">
            <div class="item">
              <div class="left">
                <div class="icon gray">↗</div>
                <div style="min-width:0">
                  <div class="title">Kraken ↔ Amplitude link</div>
                  <div class="desc">Analytics mapping for this stage</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    `;
  }

  // AWS panel
  _renderAwsPanel() {
    const awsAccountNumber = this.environment?.awsAccountNumber || '';
    const awsRegion = this.environment?.region || 'us-east-1';

    return html`
      <div class="grid">
        <!-- AWS Account Configuration Card -->
        <div class="card subtle" style="grid-column: span 5;">
          <div class="split">
            <div>
              <h4>AWS Account</h4>
              <p class="hint">Configure the AWS account number for this stage to enable quick links to AWS console.</p>
            </div>
            <span class="badge ${awsAccountNumber ? '' : 'warn'}">
              <span class="dot ${awsAccountNumber ? 'good' : 'warn'}"></span>
              ${awsAccountNumber ? 'Configured' : 'Not configured'}
            </span>
          </div>

          <form id="aws-account-form" @submit="${this._saveAwsAccountConfig}">
            <div class="field">
              <label for="awsAccountNumber">AWS Account Number</label>
              <input type="text" id="awsAccountNumber" placeholder="e.g., 123456789012" .value="${awsAccountNumber}" pattern="[0-9]{12}" title="AWS account number must be 12 digits" />
            </div>

            <div class="row">
              <button type="submit" class="btn primary">Save</button>
            </div>
          </form>
        </div>

        <!-- AWS Services Links Card -->
        <div class="card subtle" style="grid-column: span 7;">
          <div class="split">
            <div>
              <h4>AWS Services</h4>
              <p class="hint">Quick links to AWS console for this stage's account.</p>
            </div>
            ${awsAccountNumber ? html`
              <a href="${`https://d-9067e56330.awsapps.com/start/#/console?account_id=${awsAccountNumber}&role_name=AdministratorAccess`}" target="_blank" rel="noopener" class="btn primary">
                Sign in to AWS
              </a>
            ` : ''}
          </div>
          ${awsAccountNumber ? html`
            <div class="aws-services-grid">
              ${this._getAwsServiceLinks(awsAccountNumber, awsRegion).map(service => html`
                <a href="${service.url}" target="_blank" rel="noopener" class="aws-service-link">
                  <img src="${service.icon}" alt="${service.name}" class="aws-service-icon" />
                  <span class="aws-service-name">${service.name}</span>
                </a>
              `)}
            </div>
          ` : html`
            <div class="hint" style="margin-top: 12px; padding: 16px; background: #f3f4f6; border-radius: 8px; text-align: center;">
              Enter an AWS account number to enable quick links.
            </div>
          `}
        </div>
      </div>
    `;
  }

  // Settings panel
  _renderSettingsPanel() {
    const waveSettings = this._healthStatus?.waveHealth?.settings;
    const awsRegion = this.environment?.region || 'us-east-1';
    const fussRegion = this.environment?.fussRegion || 'us-east-1';
    const waveUrlOverride = this.environment?.waveUrl;
    const waveUrl = waveUrlOverride || this._buildWaveApiUrl(this.environmentId);
    const fussUrl = this.environment?.fussUrl || this._buildFussApiUrl(this.environmentId);
    const awsAccountNumber = this.environment?.awsAccountNumber || '';

    return html`
      <div class="grid" style="margin-bottom: 24px;">
        <div class="card subtle" style="grid-column: span 12;">
          <h4>Environment Configuration</h4>
          <p class="hint">Core settings that control how Kraken connects to this environment.</p>

          <form id="environment-config-form" @submit="${this._saveEnvironmentConfig}">
            <div class="field">
              <label for="region">Region</label>
              <select id="region">
                <option value="us-east-1" ?selected="${awsRegion === 'us-east-1'}">us-east-1</option>
                <option value="ca-central-1" ?selected="${awsRegion === 'ca-central-1'}">ca-central-1</option>
                <option value="eu-west-1" ?selected="${awsRegion === 'eu-west-1'}">eu-west-1</option>
              </select>
            </div>

            <div class="field">
              <label for="fussRegion">FUSS Region</label>
              <select id="fussRegion">
                <option value="us-east-1" ?selected="${fussRegion === 'us-east-1'}">us-east-1</option>
                <option value="ca-central-1" ?selected="${fussRegion === 'ca-central-1'}">ca-central-1</option>
                <option value="eu-west-1" ?selected="${fussRegion === 'eu-west-1'}">eu-west-1</option>
              </select>
            </div>

            <div class="field">
              <label for="waveUrl">Wave URL override</label>
              <input type="url" id="waveUrl" placeholder="Leave blank to use derived URL" .value="${waveUrlOverride || ''}" />
            </div>

            <div class="row">
              <button type="submit" class="btn primary">Save</button>
            </div>
          </form>
        </div>
      </div>

      <div class="split" style="margin-bottom: 16px;">
        <p class="hint" style="margin: 0;">Configuration values for this environment (read-only).</p>
        <div class="row" style="margin-top:0">
          <button class="btn">Export</button>
        </div>
      </div>

      <table aria-label="Settings table">
        <thead>
          <tr>
            <th>Key</th>
            <th>Value</th>
            <th>Source</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td class="mono">wave.baseUrl</td>
            <td class="mono">${waveUrl}</td>
            <td>${waveUrlOverride ? 'override' : 'env config'}</td>
          </tr>
          <tr>
            <td class="mono">fuss.baseUrl</td>
            <td class="mono">${fussUrl}</td>
            <td>env config</td>
          </tr>
          ${awsAccountNumber ? html`
            <tr>
              <td class="mono">aws.accountNumber</td>
              <td class="mono">${awsAccountNumber}</td>
              <td>env config</td>
            </tr>
          ` : ''}
          ${this.environment?.amplitudeProjectId ? html`
            <tr>
              <td class="mono">amplitude.projectId</td>
              <td class="mono">${this.environment.amplitudeProjectId}</td>
              <td>integration</td>
            </tr>
          ` : ''}
          <tr>
            <td class="mono">region</td>
            <td class="mono">${awsRegion}</td>
            <td>env config</td>
          </tr>
          <tr>
            <td class="mono">fussRegion</td>
            <td class="mono">${fussRegion}</td>
            <td>env config</td>
          </tr>
          ${waveSettings ? html`
            <tr>
              <td class="mono">wave.emailEnabled</td>
              <td class="mono">${waveSettings.emailEnabled ? 'true' : 'false'}</td>
              <td>wave runtime</td>
            </tr>
          ` : ''}
        </tbody>
      </table>
    `;
  }

  _getAwsServiceLinks(accountNumber, region) {
    const baseUrl = `https://${region}.console.aws.amazon.com`;
    // AWS Architecture Icons from official AWS icon pack
    const iconBase = '/assets/img/aws';
    return [
      {
        name: 'CloudWatch',
        icon: `${iconBase}/cloudwatch.svg`,
        url: `${baseUrl}/cloudwatch/home?region=${region}#logsV2:log-groups`,
      },
      {
        name: 'Lambda',
        icon: `${iconBase}/lambda.svg`,
        url: `${baseUrl}/lambda/home?region=${region}#/functions`,
      },
      {
        name: 'DynamoDB',
        icon: `${iconBase}/dynamodb.svg`,
        url: `${baseUrl}/dynamodbv2/home?region=${region}#tables`,
      },
      {
        name: 'S3',
        icon: `${iconBase}/s3.svg`,
        url: `https://s3.console.aws.amazon.com/s3/buckets?region=${region}`,
      },
      {
        name: 'CloudFormation',
        icon: `${iconBase}/cloudformation.svg`,
        url: `${baseUrl}/cloudformation/home?region=${region}#/stacks`,
      },
      {
        name: 'API Gateway',
        icon: `${iconBase}/api-gateway.svg`,
        url: `${baseUrl}/apigateway/main/apis?region=${region}`,
      },
      {
        name: 'Secrets Manager',
        icon: `${iconBase}/secrets-manager.svg`,
        url: `${baseUrl}/secretsmanager/listsecrets?region=${region}`,
      },
      {
        name: 'Route 53',
        icon: `${iconBase}/route53.svg`,
        url: 'https://us-east-1.console.aws.amazon.com/route53/v2/hostedzones',
      },
    ];
  }

  async _saveAwsAccountConfig(e) {
    e.preventDefault();
    const form = this.shadowRoot.getElementById('aws-account-form');
    const awsAccountNumber = form.querySelector('#awsAccountNumber').value;

    try {
      await this.client.updateEnvironmentConfig(this.environmentId, { awsAccountNumber });
      this.environment = { ...this.environment, awsAccountNumber };
      this._toast('AWS account number saved');
    } catch (error) {
      console.error('Error saving AWS account config:', error);
      this._toast('Failed to save AWS account config');
    }
  }

  // Tasks panel
  _renderTasksPanel() {
    if (!this.hasTaskManagement) {
      return html`
        <div class="card subtle">
          <h4>Tasks not available</h4>
          <p>Task management is not enabled for this environment.</p>
          <p class="hint">This environment needs the TASK-MANAGEMENT tag to enable task features.</p>
        </div>
      `;
    }

    return html`
      <nova-task-manager .environmentId="${this.environmentId}"></nova-task-manager>
    `;
  }

  // Remove dialog
  _renderRemoveDialog() {
    return html`
      <d2l-dialog id="remove-environment-dialog" title-text="Remove environment">
        <div class="dialog-content">
          We don't currently have automatic teardowns hooked up - please manually teardown your stack before removing it from kraken <br><br>

          <b>Teardown via command line:</b> <br>
          'npx serverless remove --stage=stageName'
          <br><br>
          <b>Teardown via AWS GUI:</b> <br>
          In CloudFormation, navigate to the stack and click into it. In the top right corner you'll see a 'Delete' button which will trigger off the teardown.
        </div>
        <d2l-button slot="footer" @click="${this._handleRemoveClick}" primary data-dialog-action="done">Serverless Teardown Complete</d2l-button>
        <d2l-button slot="footer" data-dialog-action>Cancel</d2l-button>
      </d2l-dialog>
    `;
  }

  // Toast
  _renderToast() {
    return html`
      <div class="toast ${this._toastVisible ? 'show' : ''}" role="status" aria-live="polite">
        <span class="dot good"></span>
        <span>${this._toastMessage}</span>
      </div>
    `;
  }

  render() {
    const isSimpleMode = this._isDemoResetEnv && !this._advancedView;
    return html`
      <div class="shell-app">
        ${this._renderHeader()}
        <div class="shell-content ${isSimpleMode ? 'no-sidebar' : ''}">
          ${isSimpleMode ? '' : this._renderSidebar()}
          ${this._renderMainContent()}
        </div>
        ${this._renderRemoveDialog()}
        ${this._renderToast()}
      </div>
    `;
  }
}

window.customElements.define('pm-environment-shell', EnvironmentShell);

